// SPDX-License-Identifier: Elastic-2.0
/**
 * Page — centred content wrapper for route bodies.
 *
 * Caps width (default 1280px), centres horizontally, applies a token-based
 * padding scale, and optionally renders an `h1` title using the Inter h4
 * scale (per design — the displayed page heading in the dashboard chrome).
 *
 * The title is intentionally **not** wired to the document title — that's
 * the routing layer's responsibility (T-004 already handles `<title>`).
 */
import type { HTMLAttributes, ReactNode } from "react";

import styles from "./Page.module.css";

export type PagePadding = "sm" | "md" | "lg";

export interface PageProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Optional visible page title (renders as `<h1>`). */
  title?: ReactNode;
  /** Maximum content width in pixels. Defaults to 1280. */
  maxWidth?: number;
  /** Padding scale. Defaults to `md`. */
  padding?: PagePadding;
  children?: ReactNode;
}

const PAD_CLASS: Record<PagePadding, string> = {
  sm: styles.padSm,
  md: styles.padMd,
  lg: styles.padLg,
};

export function Page({
  title,
  maxWidth = 1280,
  padding = "md",
  className,
  style,
  children,
  ...rest
}: PageProps): JSX.Element {
  const composed = [styles.page, PAD_CLASS[padding], className].filter(Boolean).join(" ");
  return (
    <div className={composed} style={{ maxWidth, ...style }} {...rest}>
      {title !== undefined && <h1 className={styles.title}>{title}</h1>}
      {children}
    </div>
  );
}
